import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import connectApi from '../../api/connectApi'
import {
    constants,
    extractVillages,
    internetConnect,
    VillageType
} from '../../helpers'
import {
    sendMessageToWorker,
    sendMessageToWorkerWithResponse
} from '../../helpers/communication'
import {
    hasLocalDataExpired,
    updateLocalDataExpirationTime
} from '../../datastores/LocalDataExpiration'
import { VeType } from '../../storeTypes/vesTypes'

const FETCH_ERROR = 'Un problème est survenu'

export type VesFetchPayload = {
    ves: VeType[]
    unassignedVillages: VillageType[]
}

const emptyVesPayload = (): VesFetchPayload => ({
    ves: [],
    unassignedVillages: []
})

export const normalizeVesPayload = (
    value: VesFetchPayload | VeType[] | null | undefined
): VesFetchPayload => {
    if (Array.isArray(value)) {
        return { ves: value, unassignedVillages: [] }
    }
    if (!value || typeof value !== 'object') {
        return emptyVesPayload()
    }
    return {
        ves: Array.isArray(value.ves) ? value.ves : [],
        unassignedVillages: Array.isArray(value.unassignedVillages)
            ? value.unassignedVillages
            : []
    }
}

const cachedUnassignedVillages = async (
    zoneCode: string
): Promise<VillageType[]> => {
    const cached = await sendMessageToWorkerWithResponse({
        [constants.MESSAGES.GET_VES]: zoneCode
    })
    return normalizeVesPayload(cached as VesFetchPayload | VeType[] | null)
        .unassignedVillages
}

const villagesForZone = (
    ves: VeType[],
    unassignedVillages: VillageType[]
): VillageType[] => {
    const fromVes = extractVillages(ves) as VillageType[]
    const seen = new Set(
        fromVes
            .map((village) => village.code)
            .filter((code): code is string => Boolean(code))
    )
    const extras = unassignedVillages.filter(
        (village) => village.code && !seen.has(village.code)
    )
    return [...fromVes, ...extras]
}

const vesFromAssignedVillages = (villages: VillageType[] = []): VeType[] => {
    const seen = new Set<string>()
    const ves: VeType[] = []
    villages.forEach((village) => {
        const villageVes = village.ves || []
        villageVes.forEach((ve) => {
            if (!ve.code || seen.has(ve.code)) {
                return
            }
            seen.add(ve.code)
            ves.push(ve as VeType)
        })
    })
    return ves
}

export const fetchVesAction = createAsyncThunk<
    VesFetchPayload,
    string,
    { rejectValue: string }
>('ves/fetchVesAction', async (zoneCode: string, { rejectWithValue }) => {
    try {
        if (!hasLocalDataExpired(constants.VES_EXPIRATION_NAME, zoneCode)) {
            const localVes = await sendMessageToWorkerWithResponse({
                [constants.MESSAGES.GET_VES]: zoneCode
            })
            return normalizeVesPayload(
                localVes as VesFetchPayload | VeType[] | null
            )
        }

        if (internetConnect()) {
            const [vesResult, villagesResult] = await Promise.allSettled([
                connectApi.get(constants.endpoints.VES, {
                    params: { zone_code: zoneCode }
                }),
                connectApi.get(constants.endpoints.ZONE_VILLAGES, {
                    params: { zone_code: zoneCode }
                })
            ])
            if (vesResult.status === 'rejected') {
                return rejectWithValue(FETCH_ERROR)
            }
            const vesResponse = vesResult.value
            // connectApi resolves HTTP errors. Callers have to read status.
            if (
                !vesResponse ||
                vesResponse.status >= 400 ||
                vesResponse.status === 0
            ) {
                return rejectWithValue(FETCH_ERROR)
            }
            const remoteVes = Array.isArray(vesResponse.data)
                ? vesResponse.data
                : []
            const villageData =
                villagesResult.status === 'fulfilled'
                    ? villagesResult.value.data
                    : undefined
            const villagesLoaded = Array.isArray(villageData)
            // A failed villages request falls back to the cached list. The
            // read happens before STORE_VES, which rewrites the zone's records.
            const unassignedVillages: VillageType[] = villagesLoaded
                ? villageData
                : await cachedUnassignedVillages(zoneCode)

            sendMessageToWorker({
                [constants.MESSAGES.STORE_VES]: {
                    ves: remoteVes,
                    zoneCode,
                    // undefined tells storeVes to keep the cached record.
                    unassignedVillages: villagesLoaded
                        ? unassignedVillages
                        : undefined
                }
            })

            // Only extend the cache when both lists loaded, so a failed
            // villages request is retried on the next load.
            if (villagesLoaded) {
                updateLocalDataExpirationTime(
                    constants.VES_EXPIRATION_NAME,
                    zoneCode,
                    constants.VES_EXPIRATION_DURATION
                )
            }

            return { ves: remoteVes, unassignedVillages }
        }

        return rejectWithValue(FETCH_ERROR)
    } catch {
        return rejectWithValue(FETCH_ERROR)
    }
})

export const fetchAssignedVillagesAction = createAsyncThunk<
    VillageType[],
    number,
    { rejectValue: string }
>(
    'ves/fetchAssignedVillagesAction',
    async (userId: number, { rejectWithValue }) => {
        try {
            const expirationKey = String(userId)
            if (
                !hasLocalDataExpired(
                    constants.ASSIGNED_VILLAGES_EXPIRATION_NAME,
                    expirationKey
                )
            ) {
                const localVillages = await sendMessageToWorkerWithResponse({
                    [constants.MESSAGES.GET_ASSIGNED_VILLAGES]: userId
                })
                // null means nothing was stored. An empty array is a real
                // assignment, so only that should skip the network.
                if (Array.isArray(localVillages)) {
                    return localVillages
                }
            }

            if (!internetConnect()) {
                return rejectWithValue(FETCH_ERROR)
            }

            const response = await connectApi.get(
                constants.endpoints.ASSIGNED_VILLAGES
            )
            if (!Array.isArray(response.data)) {
                return rejectWithValue(FETCH_ERROR)
            }

            sendMessageToWorker({
                [constants.MESSAGES.STORE_ASSIGNED_VILLAGES]: {
                    userId,
                    villages: response.data
                }
            })
            updateLocalDataExpirationTime(
                constants.ASSIGNED_VILLAGES_EXPIRATION_NAME,
                expirationKey,
                constants.ASSIGNED_VILLAGES_EXPIRATION_DURATION
            )

            return response.data
        } catch {
            return rejectWithValue(FETCH_ERROR)
        }
    }
)

type VesStateType = {
    loading: boolean
    ves: VeType[]
    villages: VillageType[]
    error?: string
}

const initialState: VesStateType = {
    loading: false,
    ves: [],
    villages: [],
    error: ''
}

export const vesSlice = createSlice({
    name: 'ves',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchVesAction.pending, (state) => {
                state.loading = true
            })
            .addCase(fetchVesAction.fulfilled, (state, action) => {
                const { ves, unassignedVillages } = normalizeVesPayload(
                    action.payload
                )
                state.loading = false
                state.ves = ves
                state.villages = villagesForZone(ves, unassignedVillages)
            })
            .addCase(fetchVesAction.rejected, (state, action) => {
                state.loading = false
                state.error = action.payload ?? FETCH_ERROR
            })
            .addCase(fetchAssignedVillagesAction.pending, (state) => {
                state.loading = true
            })
            .addCase(fetchAssignedVillagesAction.fulfilled, (state, action) => {
                state.loading = false
                state.villages = action.payload
                state.ves = vesFromAssignedVillages(action.payload)
            })
            .addCase(fetchAssignedVillagesAction.rejected, (state, action) => {
                state.loading = false
                state.error = action.payload ?? FETCH_ERROR
            })
    }
})

export default vesSlice.reducer
